import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import authApi from '@/api/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

const buildSchema = (t: (key: string) => string) =>
  z.object({
    email: z.string().min(1, t('validation:required')).email(t('validation:email_invalid')),
  });

type FormData = z.infer<ReturnType<typeof buildSchema>>;

export default function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const { t: tValidation } = useTranslation('validation');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tCombined = (key: string) =>
    key.startsWith('validation:') ? tValidation(key.slice('validation:'.length)) : t(key);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(buildSchema(tCombined)),
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      await authApi.forgotPassword(data.email);
    } finally {
      setIsSubmitting(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4">
      <Card className="w-full max-w-md rounded-[14px] border-border-c shadow-card-lift">
        <CardHeader className="space-y-2 text-center">
          <CardTitle className="font-brand text-[22px] font-extrabold tracking-[-0.015em] text-ink">
            {t('forgot_password.title')}
          </CardTitle>
          <CardDescription className="text-[12.5px] text-muted-foreground">
            {t('forgot_password.subtitle')}
          </CardDescription>
        </CardHeader>

        {submitted ? (
          <CardContent className="space-y-4">
            <Alert>
              <AlertDescription>{t('forgot_password.generic_response')}</AlertDescription>
            </Alert>
            <Link
              to="/login"
              className="block text-center text-sm text-muted-foreground hover:text-foreground"
            >
              {t('forgot_password.back_to_login')}
            </Link>
          </CardContent>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">{t('forgot_password.email_label')}</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  autoComplete="username"
                  aria-invalid={!!errors.email}
                  disabled={isSubmitting}
                  {...register('email')}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-3">
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('forgot_password.submitting')}
                  </>
                ) : (
                  t('forgot_password.submit_button')
                )}
              </Button>
              <Link
                to="/login"
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                {t('forgot_password.back_to_login')}
              </Link>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  );
}
